import { AddToPlaylist } from './extras-pages';
import { PageLoader } from '@/components/ui/loader';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PhoneIncoming, PhoneOutgoing, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select } from '@/components/ui/misc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useSession } from '@/features/auth/use-session';
import { usePermission } from '@/hooks/use-permission';
import { useUserOptions } from '@/hooks/use-user-options';
import { formatDateTime } from '@/lib/format';
import { CALL_OUTCOMES, formatDuration, recordPath, useCalls, useDeleteCall } from './api';

const OUTCOME_COLOUR: Record<string, string> = {
  CONNECTED: 'bg-emerald-100 text-emerald-700',
  NO_ANSWER: 'bg-neutral-200 text-neutral-600',
  BUSY: 'bg-amber-100 text-amber-700',
  VOICEMAIL: 'bg-blue-100 text-blue-700',
  WRONG_NUMBER: 'bg-red-100 text-red-700',
  CALLBACK: 'bg-violet-100 text-violet-700',
};

/** The call log (AmoCRM and HubSpot): every logged call, by whom and how it went. */
export function CallsPage() {
  const { scopeOf } = usePermission();
  const { data: me } = useSession();
  const { data: users = [] } = useUserOptions();
  const [userId, setUserId] = useState('');
  const [outcome, setOutcome] = useState('');
  const [days, setDays] = useState(30);
  const from = useMemo(() => new Date(Date.now() - days * 86_400_000).toISOString(), [days]);
  const { data: calls = [], isLoading } = useCalls({ userId: userId || undefined, outcome: outcome || undefined, from });
  const remove = useDeleteCall();
  const wide = scopeOf('crm.call.view') !== 'OWN';

  const connected = calls.filter((call) => call.outcome === 'CONNECTED').length;
  const talk = calls.reduce((sum, call) => sum + call.durationSeconds, 0);

  return (
    <div>
      <PageHeader
        title="Calls"
        description="Click Call on any lead, contact or deal to dial from your phone and log it. Every call lands here and on the record."
        breadcrumbs={[{ label: 'Sales hub' }, { label: 'Calls' }]}
      />
      <div className="mb-4 grid grid-cols-3 gap-3">
        {(
          [
            ['Calls', calls.length],
            ['Connected', calls.length ? `${Math.round((connected / calls.length) * 100)}%` : '—'],
            ['Talk time', formatDuration(talk)],
          ] as const
        ).map(([label, value]) => (
          <Card key={label}>
            <CardContent className="pt-5">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
              <p className="text-2xl font-extrabold">{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="mb-4 flex flex-wrap gap-2">
        <Select className="w-40" value={days} onChange={(event) => setDays(Number(event.target.value))}>
          {[7, 30, 90, 365].map((value) => (
            <option key={value} value={value}>
              Last {value} days
            </option>
          ))}
        </Select>
        {wide && (
          <Select className="w-48" value={userId} onChange={(event) => setUserId(event.target.value)}>
            <option value="">Everyone</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </Select>
        )}
        <Select className="w-48" value={outcome} onChange={(event) => setOutcome(event.target.value)}>
          <option value="">Any outcome</option>
          {CALL_OUTCOMES.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
      </div>
      <Card>
        <CardContent className="overflow-x-auto pt-4">
          {isLoading ? (
            <PageLoader />
          ) : calls.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No calls in this period.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Call</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead>Length</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {calls.map((call) => {
                  const path = recordPath(call.entityType, call.entityId);
                  const Icon = call.direction === 'INBOUND' ? PhoneIncoming : PhoneOutgoing;
                  return (
                    <TableRow key={call.id}>
                      <TableCell>
                        <span className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-muted-foreground" />
                          <span>
                            {path ? (
                              <Link className="font-medium text-link hover:underline" to={path}>
                                {call.phone || 'Open record'}
                              </Link>
                            ) : (
                              <span className="font-medium">{call.phone || '—'}</span>
                            )}
                            <span className="block text-xs text-muted-foreground">{call.user?.name}</span>
                          </span>
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={OUTCOME_COLOUR[call.outcome]}>{call.outcomeLabel}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{formatDuration(call.durationSeconds)}</TableCell>
                      <TableCell className="max-w-xs truncate text-sm text-muted-foreground">{call.notes}</TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{formatDateTime(call.startedAt)}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        {call.recordingUrl && (
                          <>
                            <a href={call.recordingUrl} target="_blank" rel="noreferrer" className="text-link me-1 text-xs font-semibold hover:underline">
                              Play
                            </a>
                            <AddToPlaylist callId={call.id} />
                          </>
                        )}
                        {call.userId === me?.id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.confirm('Delete this call log?') && remove.mutate(call.id)}
                          >
                            <Trash2 />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
